import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Board from "../components/Board";
import CoachBanner from "../components/CoachBanner";
import { getLegalMoves, applyMove, playerHasCapture } from "../game/engine";
import { TUTORIAL_STEPS } from "../game/tutorial";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, GraduationCap, RotateCcw } from "lucide-react";

export default function Tutorial() {
  const [stepIndex, setStepIndex] = useState(0);
  const [board, setBoard] = useState(() => TUTORIAL_STEPS[0].getBoard?.() || null);
  const [turn, setTurn] = useState("A");
  const [selected, setSelected] = useState(null);
  const [lastMove, setLastMove] = useState(null);
  const [stepDone, setStepDone] = useState(false);

  const step = TUTORIAL_STEPS[stepIndex];
  const isPlay = step.type === "play";

  const loadStep = (index) => {
    const s = TUTORIAL_STEPS[index];
    setStepIndex(index);
    setSelected(null);
    setLastMove(null);
    setStepDone(false);
    if (s.getBoard) setBoard(s.getBoard());
    setTurn(s.turn || "A");
  };

  const allLegal = useMemo(() => {
    if (!isPlay || !board || stepDone) return [];
    return getLegalMoves(board, turn);
  }, [board, turn, isPlay, stepDone]);

  const mustCapturePieces = useMemo(() => {
    if (!board || !isPlay || stepDone) return new Set();
    if (!playerHasCapture(board, turn)) return new Set();
    const out = new Set();
    for (const m of allLegal) if (m.captures.length) out.add(`${m.from[0]},${m.from[1]}`);
    return out;
  }, [board, turn, allLegal, isPlay, stepDone]);

  const legalForSelected = useMemo(() => {
    if (!selected) return [];
    return allLegal.filter((m) => m.from[0] === selected[0] && m.from[1] === selected[1]);
  }, [allLegal, selected]);

  const onClickIntersection = (x, y) => {
    if (!isPlay || stepDone || !board) return;
    const piece = board[y][x];
    if (piece && piece.owner === turn) {
      if (mustCapturePieces.size > 0 && !mustCapturePieces.has(`${x},${y}`)) {
        toast.warning("Capture obligatoire — choisissez un pion orange.");
        return;
      }
      setSelected([x, y]);
      return;
    }
    if (selected) {
      const m = legalForSelected.find((mv) => mv.to[0] === x && mv.to[1] === y);
      if (!m) return;
      if (step.validateMove && !step.validateMove(m)) {
        toast.info("Ce n'est pas le coup attendu — relisez l'instruction.");
        return;
      }
      const newBoard = applyMove(board, m);
      setBoard(newBoard);
      setLastMove({ from: m.from, to: m.to });
      setSelected(null);
      setStepDone(true);
      toast.success(step.success || "Bravo !");
    }
  };

  const next = () => {
    if (stepIndex < TUTORIAL_STEPS.length - 1) loadStep(stepIndex + 1);
  };

  const prev = () => {
    if (stepIndex > 0) loadStep(stepIndex - 1);
  };

  const resetPlayStep = () => {
    if (step.getBoard) {
      setBoard(step.getBoard());
      setSelected(null);
      setLastMove(null);
      setStepDone(false);
    }
  };

  return (
    <div className="min-h-screen bg-tribal">
      <Navbar />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-8 fade-up">
        <p className="text-xs tracking-[0.3em] text-[color:var(--primary)] flex items-center gap-2 mb-2">
          <GraduationCap size={14} /> TUTORIEL
        </p>
        <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight mb-1">{step.title}</h1>
        <p className="text-xs text-[color:var(--text-secondary)] mb-4">
          Étape {stepIndex + 1} / {TUTORIAL_STEPS.length}
        </p>

        <CoachBanner message={step.instruction} />

        {isPlay && board && (
          <>
            {!stepDone && (
              <p className="text-xs text-center text-[color:var(--primary)] mb-2">
                Jouez le coup indiqué sur le plateau.
              </p>
            )}
            {stepDone && (
              <p className="text-xs text-center text-emerald-400 mb-2 font-medium">
                {step.success}
              </p>
            )}
            <Board
              board={board}
              selectedFrom={selected}
              legalMovesForSelected={legalForSelected}
              mustCapturePieces={mustCapturePieces}
              onClickIntersection={onClickIntersection}
              lastMove={lastMove}
              hintsOn={!stepDone}
            />
            {isPlay && (
              <button
                type="button"
                onClick={resetPlayStep}
                className="mt-3 mx-auto flex items-center gap-2 text-xs text-[color:var(--text-secondary)] hover:text-[color:var(--text-primary)]"
                data-testid="tutorial-reset-step"
              >
                <RotateCcw size={12} /> Recommencer cette étape
              </button>
            )}
          </>
        )}

        <div className="flex items-center justify-between gap-3 mt-8">
          <button
            type="button"
            onClick={prev}
            disabled={stepIndex === 0}
            className="px-4 py-2 rounded-md border border-[color:var(--border)] text-sm flex items-center gap-2 disabled:opacity-40 hover:bg-[color:var(--surface)]"
            data-testid="tutorial-prev"
          >
            <ChevronLeft size={16} /> Précédent
          </button>
          <div className="flex gap-1">
            {TUTORIAL_STEPS.map((_, i) => (
              <span
                key={i}
                className={`w-2 h-2 rounded-full ${i === stepIndex ? "bg-[color:var(--primary)]" : "bg-[color:var(--border)]"}`}
              />
            ))}
          </div>
          {stepIndex < TUTORIAL_STEPS.length - 1 ? (
            <button
              type="button"
              onClick={next}
              disabled={isPlay && !stepDone}
              className="px-4 py-2 rounded-md btn-gold text-sm flex items-center gap-2 disabled:opacity-40"
              data-testid="tutorial-next"
            >
              Suivant <ChevronRight size={16} />
            </button>
          ) : (
            <Link
              to="/game/ai"
              className="px-4 py-2 rounded-md btn-gold text-sm flex items-center gap-2"
              data-testid="tutorial-play-ai"
            >
              Jouer contre l&apos;IA
            </Link>
          )}
        </div>

        <div className="mt-6 flex flex-wrap gap-4 justify-center text-xs">
          <Link to="/rules" className="text-[color:var(--text-secondary)] hover:text-[color:var(--text-primary)]">
            Règles complètes
          </Link>
          <Link to="/lobby" className="text-[color:var(--text-secondary)] hover:text-[color:var(--text-primary)]">
            ← Salon
          </Link>
        </div>
      </main>
    </div>
  );
}
